import {
    AnimationComponent,
    Component,
    LightComponent,
    MeshComponent,
    Object3D,
} from '@wonderlandengine/api';
import {vec3} from 'gl-matrix';
import {delay} from '../utils/delay.js';
import {TextBubbles} from '../components/text-bubbles.js';
import {GameManagerInstance, SceneNames} from '../managers/gamemanager.js';
import {property} from '@wonderlandengine/api/decorators.js';
import {Blink} from '../components/blink.js';
import {Particles} from '../components/particles.js';
import {PhongMaterial} from '../types/materials.js';
import {TextFade} from '../components/text-fade.js';
import {Scene2LdaDoor} from './scene2-lda-door.js';
import {SoundManagerInstance, Sounds} from '../managers/soundmanager.js';
import {SnowParticles} from '../components/snow-particles.js';
/**
 * level-manager-scene-opening
 */
export class LevelManagerSceneOpening extends Component {
    static TypeName = 'level-manager-scene-opening';
    /* Properties that are configurable in the editor */

    /* Scene1 */
    @property.object({required: true})
    scene1!: Object3D;
    @property.object({required: true})
    scene1_text!: Object3D;
    @property.object({required: true})
    scene1_light1!: Object3D;
    /* Scene2 */
    @property.object({required: true})
    scene2!: Object3D;
    @property.object({required: true})
    scene2_text!: Object3D;
    @property.object({required: true})
    scene2_light1!: Object3D;
    @property.object({required: true})
    scene2_pictures!: Object3D;
    @property.object({required: true})
    scene2_killerParent!: Object3D;
    @property.object({required: true})
    scene2_killerMesh!: Object3D;
    @property.object({required: true})
    scene2_killerAnim!: Object3D;
    @property.object({required: true})
    scene2_movePosEnd!: Object3D;
    @property.object({required: true})
    scene2_smokeParticle!: Object3D;
    @property.object({required: true})
    scene2_photo1!: Object3D;
    @property.object({required: true})
    scene2_photo2!: Object3D;
    @property.object({required: true})
    scene2_photo3!: Object3D;

    /* Scene3 */
    @property.object({required: true})
    scene3!: Object3D;
    @property.object({required: true})
    scene3_text!: Object3D;
    @property.object({required: true})
    scene3_door!: Object3D;
    @property.object({required: true})
    /* Scene4 */
    @property.object({required: true})
    scene4!: Object3D;
    @property.object({required: true})
    scene4_text!: Object3D;
    @property.object({required: true})
    scene4_patricaAnim!: Object3D;
    @property.object({required: true})
    scene4_light1!: Object3D;
    /* Scene5 */
    @property.object({required: true})
    scene5!: Object3D;
    @property.object({required: true})
    scene5_text!: Object3D;
    /* Scene6 */
    @property.object({required: true})
    scene6!: Object3D;
    @property.object({required: true})
    scene6_text!: Object3D;
    @property.object({required: true})
    scene6_inspectedSphere!: Object3D;
    @property.object({required: true})
    scene6_clouds!: Object3D;
    /* Scene7 */
    @property.object({required: true})
    scene7!: Object3D;
    @property.object({required: true})
    patricaHead!: Object3D;

    @property.object({required: true})
    snowParticleObject!: Object3D;

    private player?: Object3D;
    private blinkSphere?: Object3D;
    private scene5_startGhostsRot = false;
    private scene6_startCloudMove = false;
    private lightLerpTime = 0;
    private lightOn = false;
    private lightSpeedSeconds = 0;
    private lightMax = vec3.create();
    private lightMin = vec3.create();
    private scene1_startLightLerp = false;
    private moveLerpTime = 0;
    private scene2_startLightLerp = false;
    private scene2_startMoveLerp = false;
    private scene4_startLightLerp = false;
    private lightLerpRatio = 0;
    private lightLerpResult = vec3.create();
    private moveLerpRatio = 0;
    private moveSpeedSeconds = 0;
    private moveLerpResult = vec3.create();
    private movePosStart = vec3.create();
    private movePosEnd = vec3.create();
    private blinkComponent!: Blink;
    private smokeParticles!: Particles;
    private snowParticles!: SnowParticles;

    start() {
        /* Component references */
        const tempSmokeParticles = this.scene2_smokeParticle.getComponent(Particles);
        if (!tempSmokeParticles) {
            throw new Error(
                `${LevelManagerSceneOpening.TypeName}(${this.object.name}): scene2_smokeParticle does not have a particles component`
            );
        }
        this.smokeParticles = tempSmokeParticles;

        /* General */
        this.player =
            this.object; /* So that 'text-bubbles' can reference it without manually ref the player for each bubble */

        const tempBlinkSphere = this.object.getComponent(Blink);
        if (!tempBlinkSphere) {
            throw new Error(
                `${LevelManagerSceneOpening.TypeName}(${this.object.name})No Blink Component found on LevelManagerSceneOpening`
            );
        }
        this.blinkComponent = tempBlinkSphere;
        this.blinkSphere = tempBlinkSphere.blinkSphere;
        if (!this.blinkSphere) {
            throw new Error(
                `${LevelManagerSceneOpening.TypeName}(${this.object.name})No Blink Sphere found on Blink component`
            );
        }
        const tempMeshComponent = this.blinkSphere.getComponent(MeshComponent);
        if (!tempMeshComponent) {
            throw new Error(
                `${LevelManagerSceneOpening.TypeName}(${this.object.name})No Mesh Component found on LevelManagerSceneOpening`
            );
        }
        tempMeshComponent.active = false;

        const tempSnowParticles = this.snowParticleObject.getComponent(SnowParticles);
        if (!tempSnowParticles) {
            throw new Error(
                `level-manager-scene-opening(${this.object.name}): snowParticleObject does not have a SnowParticles`
            );
        }
        this.snowParticles = tempSnowParticles;

        this.scene5_startGhostsRot = false;
        this.scene6_startCloudMove = false;
        this.snowParticles.active = false;
        /* Lerp Light */
        this.lightLerpTime = 100;
        this.lightOn = true;
        this.lightSpeedSeconds = 0.5;
        this.lightMax = [18.5, 0, 0];
        this.lightMin = [8, 0, 0];

        /* Array of scene parent objects */
        const sceneParents = [
            this.scene1,
            this.scene2,
            this.scene3,
            this.scene4,
            this.scene5,
            this.scene6,
            this.scene7,
        ];

        /* Deactive Scenes Meshes & Lights */
        for (const sceneParent of sceneParents) {
            if (!sceneParent) {
                console.warn(`${LevelManagerSceneOpening.TypeName}: Scene Parent is null`);
                continue;
            }
            /* Deactive children */
            this.activeScene(sceneParent, false);
        }

        /* Deactive Patrica Head Meshes */
        for (const child of this.patricaHead.children) {
            const patriciaHeadChildMesh = child.getComponent(MeshComponent);
            if (patriciaHeadChildMesh) {
                patriciaHeadChildMesh.active = false;
            }
        }
        /* Light Lerp */
        this.lightLerpTime = 0;
        this.scene1_startLightLerp = true;
        this.blinkComponent.blinkTime = 0.5;
        setTimeout(() => {
            this.blinkComponent.startBlink(
                null,
                () => {
                    /* Scene1 */
                    this.activeScene(this.scene1, true);

                    const scene1TextBubble = this.scene1_text.getComponent(TextBubbles);
                    if (!scene1TextBubble) {
                        throw new Error(
                            `${LevelManagerSceneOpening.TypeName}(${this.object.name}): scene1_text does not have a text-bubbles component`
                        );
                    }
                    scene1TextBubble.onDown(this.object, undefined); /* Text Start */

                    SoundManagerInstance.playSound(Sounds.patriciaOpening1);
                },
                true,
                1
            );
        });
    }

    update(dt: number) {
        this.lightLerpTime += dt;
        this.moveLerpTime += dt;

        /* Lerp Light */
        if (
            this.scene1_startLightLerp ||
            this.scene2_startLightLerp ||
            this.scene4_startLightLerp
        ) {
            this.startlightLerp();
        }

        /* Lerp Movement */
        if (this.scene2_startMoveLerp) {
            this.startMovementLerp();
        }

        /* Rotate Pill on Y axis based on Parent */
        if (this.scene5_startGhostsRot) {
            this.scene5.rotateAxisAngleDegObject([0, 1, 0], 10 * dt);
        }

        /* Move Clouds */
        if (this.scene6_startCloudMove) {
            this.scene6_clouds.translateLocal([-5, 0, 0.0]);
        }
    }

    async sendMessage(msg: string) {
        /* Start Scene2 */
        if (msg == 'scene1Text_End') {
            this.startScene2();
        }

        if (msg == 'scene2Text_End') {
            this.startScene3();
        }

        if (msg == 'scene3Text_End') {
            this.startScene4();
        }

        if (msg == 'scene4Text_End') {
            this.startScene5();
        }

        if (msg == 'scene5Text_End') {
            this.startScene6();
        }

        if (msg == 'scene6Text_End') {
            await this.startScene7();
        }
    }

    activeScene(scene: Object3D, activeBool: boolean) {
        for (const child of scene.children) {
            const childMesh = child.getComponent(MeshComponent);
            if (childMesh) {
                childMesh.active = activeBool;
            }
            const childLight = child.getComponent(LightComponent);
            if (childLight) {
                childLight.active = activeBool;
            }

            /* Deactive children of children */
            for (const c of child.children) {
                const subchildMesh = c.getComponent(MeshComponent);
                if (subchildMesh) {
                    subchildMesh.active = activeBool;
                }
                const subchildLight = c.getComponent(LightComponent);
                if (subchildLight) {
                    subchildLight.active = activeBool;
                }
            }
        }
    }

    startlightLerp() {
        /* Lerp Light Flicker */
        this.lightLerpRatio = this.lightLerpTime / this.lightSpeedSeconds; /* Seconds */
        if (this.lightLerpTime < this.lightSpeedSeconds) {
            if (this.lightOn) {
                /* Close Light */
                this.lightLerpResult = vec3.lerp(
                    vec3.create(),
                    this.lightMax,
                    this.lightMin,
                    this.lightLerpRatio
                );
            } else {
                /* Open Light */
                this.lightLerpResult = vec3.lerp(
                    vec3.create(),
                    this.lightMin,
                    this.lightMax,
                    this.lightLerpRatio
                );
            }
            /* Apply Result */
            if (this.scene1_startLightLerp) {
                const scene1Light1Light = this.scene1_light1.getComponent(LightComponent);
                if (!scene1Light1Light) {
                    throw new Error(
                        `${LevelManagerSceneOpening.TypeName}(${this.object.name}): scene1_light1 does not have a light component`
                    );
                }
                scene1Light1Light.intensity = this.lightLerpResult[0];
            } else if (this.scene2_startLightLerp) {
                const scene2Light1Light = this.scene2_light1.getComponent(LightComponent);
                if (!scene2Light1Light) {
                    throw new Error(
                        `${LevelManagerSceneOpening.TypeName}(${this.object.name}): scene2_light1 does not have a light component`
                    );
                }
                scene2Light1Light.intensity = this.lightLerpResult[0];
            } else if (this.scene4_startLightLerp) {
                const scene4Light1Light = this.scene4_light1.getComponent(LightComponent);
                if (!scene4Light1Light) {
                    throw new Error(
                        `${LevelManagerSceneOpening.TypeName}(${this.object.name}): scene4_light1 does not have a light component`
                    );
                }
                scene4Light1Light.intensity = this.lightLerpResult[0];
            }
        } else {
            this.lightLerpSwitch();
        }
    }

    lightLerpSwitch() {
        this.lightLerpTime = 0;
        this.lightOn = !this.lightOn; /* toggle */
    }

    startMovementLerp() {
        /* Lerp Move Objects */
        this.moveLerpRatio = this.moveLerpTime / this.moveSpeedSeconds; /* Seconds */
        if (this.moveLerpTime < this.moveSpeedSeconds) {
            /* Calculate Result */
            this.moveLerpResult = vec3.lerp(
                vec3.create(),
                this.movePosStart,
                this.movePosEnd,
                this.moveLerpRatio
            );
            /* Apply Result */
            if (this.scene2_startMoveLerp) {
                this.scene2_killerParent.setPositionWorld(this.moveLerpResult);
            }
        }
    }

    startScene2() {
        /* Blink, Deactive Scene1 & Active Scene2 */
        this.blinkComponent.startBlink(
            () => {
                this.scene1_startLightLerp = false;

                this.smokeParticles.active = true; /* Smoke Start */
                this.activeScene(this.scene1, false);
                this.activeScene(this.scene2, true);

                for (const child of this.scene2_pictures.children) {
                    const childMesh = child.getComponent('mesh');
                    if (!childMesh) {
                        throw new Error(
                            `${LevelManagerSceneOpening.TypeName}(${this.object.name}): scene2_pictures children does not have a mesh component`
                        );
                    }
                    (childMesh.material as PhongMaterial).diffuseColor = [1, 1, 1, 0];
                }
            },
            async () => {
                /* Text Start */
                const scene2TestBubbles = this.scene2_text.getComponent(TextBubbles);
                if (!scene2TestBubbles) {
                    throw new Error(
                        `${LevelManagerSceneOpening.TypeName}(${this.object.name}): scene2_text does not have a text-bubbles component`
                    );
                }
                scene2TestBubbles.onDown(this.object, undefined);

                /* Light Lerp */
                this.lightSpeedSeconds = 0.05;
                this.lightMax = [1.5, 0, 0];
                this.lightMin = [0.5, 0, 0];
                this.lightLerpTime = 0;
                this.scene2_startLightLerp = true;

                SoundManagerInstance.playSound(Sounds.patriciaOpening2);

                /* Killer Animation & Lerp Movement */
                const killerMeshComponent = this.scene2_killerMesh.getComponent('mesh');
                if (!killerMeshComponent) {
                    throw new Error(
                        `${LevelManagerSceneOpening.TypeName}(${this.object.name}): scene2_killerMesh does not have a mesh component`
                    );
                }
                killerMeshComponent.active = true;

                const killerAnimationComponent =
                    this.scene2_killerAnim.getComponent('animation');
                if (!killerAnimationComponent) {
                    throw new Error(
                        `${LevelManagerSceneOpening.TypeName}(${this.object.name}): scene2_killerAnim does not have a animation component`
                    );
                }
                killerAnimationComponent.play();
                this.movePosStart = this.scene2_killerParent.getPositionWorld();
                this.movePosEnd = this.scene2_movePosEnd.getPositionWorld();
                this.moveSpeedSeconds = 8;
                this.moveLerpTime = 0;
                this.scene2_startMoveLerp = true;

                /* Photos LerpIn */
                await delay(1.5);
                const photo1TextFade = this.scene2_photo1.getComponent(TextFade);
                if (!photo1TextFade) {
                    throw new Error(
                        `${LevelManagerSceneOpening.TypeName}(${this.object.name}): scene2_photo1 does not have a text-fade component`
                    );
                }
                photo1TextFade.fadeIn();

                await delay(1.5);
                const photo2TextFade = this.scene2_photo2.getComponent(TextFade);
                if (!photo2TextFade) {
                    throw new Error(
                        `${LevelManagerSceneOpening.TypeName}(${this.object.name}): scene2_photo2 does not have a text-fade component`
                    );
                }
                photo2TextFade.fadeIn();
                await delay(1.5);
                const photo3TextFade = this.scene2_photo3.getComponent(TextFade);
                if (!photo3TextFade) {
                    throw new Error(
                        `${LevelManagerSceneOpening.TypeName}(${this.object.name}): scene2_photo3 does not have a text-fade component`
                    );
                }
                photo3TextFade.fadeIn();
            }
        );
    }

    startScene3() {
        /* Blink, Deactive Scene2 & Active Scene3 */
        this.blinkComponent.startBlink(
            () => {
                this.smokeParticles.endParticles(); /* Smoke End */
                this.smokeParticles.active = false; /* Smoke End */
                this.scene2_startLightLerp = false;
                this.scene2_startMoveLerp = false;
                this.activeScene(this.scene2, false);
                this.activeScene(this.scene3, true);
            },
            async () => {
                /* Text Start */
                const scene3TextBubbles = this.scene3_text.getComponent(TextBubbles);
                if (!scene3TextBubbles) {
                    throw new Error(
                        `${LevelManagerSceneOpening.TypeName}(${this.object.name}): scene3_text does not have a text-bubbles component`
                    );
                }
                scene3TextBubbles.onDown(this.object, undefined);
                SoundManagerInstance.playSound(Sounds.patriciaOpening3);
                /* Lerp Door Rotation */
                await delay(1);
                const scene3Door = this.scene3_door.getComponent(Scene2LdaDoor);
                if (!scene3Door) {
                    throw new Error(
                        `${LevelManagerSceneOpening.TypeName}(${this.object.name}): scene3_door does not have a scene2-lda-door component`
                    );
                }
                await scene3Door.startRot();
            }
        );
    }

    startScene4() {
        /* Blink, Deactive Scene3 & Active Scene4 */
        this.blinkComponent.startBlink(
            () => {
                this.activeScene(this.scene3, false);
                this.activeScene(this.scene4, true);

                /* Active Patrica Head Meshes */
                for (const child of this.patricaHead.children) {
                    const patriciaHeadChildMesh = child.getComponent(MeshComponent);
                    if (patriciaHeadChildMesh) {
                        patriciaHeadChildMesh.active = true;
                    }
                }
            },
            () => {
                /* Text Start */
                const scene4TextBubbles = this.scene4_text.getComponent(TextBubbles);
                if (!scene4TextBubbles) {
                    throw new Error(
                        `${LevelManagerSceneOpening.TypeName}(${this.object.name}): scene4_text does not have a text-bubbles component`
                    );
                }
                scene4TextBubbles.onDown(this.object, undefined); /* Start Text */
                SoundManagerInstance.playSound(Sounds.patriciaOpening4);
                /* Character Animation */
                const scene4PatriciaAnimation =
                    this.scene4_patricaAnim.getComponent(AnimationComponent);
                if (!scene4PatriciaAnimation) {
                    throw new Error(
                        `${LevelManagerSceneOpening.TypeName}(${this.object.name}): scene4_patricaAnim does not have a animation component`
                    );
                }
                scene4PatriciaAnimation.play();

                /* Light Lerp */
                this.lightSpeedSeconds = 0.5;
                this.lightMax = [1.5, 0, 0];
                this.lightMin = [0.5, 0, 0];
                this.lightLerpTime = 0;
                this.scene4_startLightLerp = true;
            }
        );
    }

    startScene5() {
        this.snowParticles.active = true;
        this.snowParticles.startSpawning();
        this.activeScene(this.scene5, true);
        this.scene5_startGhostsRot = true;
        const scene5TextBubbles = this.scene5_text.getComponent(TextBubbles);
        if (!scene5TextBubbles) {
            throw new Error(
                `${LevelManagerSceneOpening.TypeName}(${this.object.name}): scene5_text does not have a text-bubbles component`
            );
        }
        scene5TextBubbles.onDown(this.object, undefined); /* Start Text */
        SoundManagerInstance.playSound(Sounds.patriciaOpening5);
    }

    startScene6() {
        SoundManagerInstance.playSound(Sounds.MoonDroning, false, 0.5, {
            from: 0,
            duration: 2000,
        });
        /* Blink, Deactive Scene4,Scene5 & Active Scene6 */
        this.blinkComponent.startBlink(
            () => {
                this.scene4_startLightLerp = false;
                this.activeScene(this.scene4, false);
                this.activeScene(this.scene5, false);
                this.activeScene(this.scene6, true);
                const scene6InspectedSphereMesh =
                    this.scene6_inspectedSphere.getComponent(MeshComponent);
                if (!scene6InspectedSphereMesh) {
                    throw new Error(
                        `${LevelManagerSceneOpening.TypeName}(${this.object.name}): scene6_inspectedSphere does not have a mesh component`
                    );
                }
                scene6InspectedSphereMesh.active = false;
                this.snowParticles.active = false;
                /* Deactive Patrica Head Meshes */
                for (const child of this.patricaHead.children) {
                    const patriciaHeadChildMesh = child.getComponent(MeshComponent);
                    if (patriciaHeadChildMesh) {
                        patriciaHeadChildMesh.active = false;
                    }
                }
            },
            () => {
                /* Text Start */
                const scene6TextBubbles = this.scene6_text.getComponent(TextBubbles);
                if (!scene6TextBubbles) {
                    throw new Error(
                        `${LevelManagerSceneOpening.TypeName}(${this.object.name}): scene6_text does not have a text-bubbles component`
                    );
                }
                scene6TextBubbles.onDown(this.object, undefined); /* Start Text */
                SoundManagerInstance.playSound(Sounds.patriciaOpening6);
                /* Move Clouds */
                this.scene6_startCloudMove = true;
            }
        );
    }

    async startScene7() {
        /* Logo */

        this.activeScene(this.scene7, true);
        this.scene7.children[0].translateLocal([0, 0, -10]);

        //Transition Clouds & Moon Colors to Red!

        /* blink to next scene */
        await delay(6);

        SoundManagerInstance.stopSound(Sounds.MoonDroning, {
            from: 0.5,
            duration: 2000,
        });

        GameManagerInstance.loadScene(SceneNames.Scene2);
    }
}
